import { Icon } from "@chakra-ui/react";
import { FaWhatsapp } from "react-icons/fa6";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { NAVBAR_GREEN_GRADIENT } from "../Button/gradients";

const MotionButton = motion.button;
const MotionDiv = motion.div;

export default function WhatsAppButton() {
  const { pathname } = useLocation();
  const [hovered, setHovered] = useState(false);
  const [stickyCtaVisible, setStickyCtaVisible] = useState(false);
  const [mobileSheetBottomRem, setMobileSheetBottomRem] = useState<number>(0);
  const [isMobileView, setIsMobileView] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const [hiddenByPage, setHiddenByPage] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const { visible } = (e as CustomEvent<{ visible: boolean }>).detail;
      if (!visible) {
        setStickyCtaVisible(false);
        return;
      }
      // Small delay to let AnimatePresence render the element first
      setTimeout(() => {
        const el = document.getElementById("sticky-cta-bar");
        const isRendered = el ? getComputedStyle(el).display !== "none" : false;
        setStickyCtaVisible(isRendered);
      }, 50);
    };
    window.addEventListener("sticky-cta-change", handler);
    return () => window.removeEventListener("sticky-cta-change", handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const { visible, bottomRem } = (e as CustomEvent<{ visible: boolean; bottomRem: number }>).detail;
      setMobileSheetBottomRem(visible ? (bottomRem ?? 6.5) : 0);
    };
    window.addEventListener("mobile-sheet-change", handler);
    return () => window.removeEventListener("mobile-sheet-change", handler);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onHide = () => setHiddenByPage(true);
    const onShow = () => setHiddenByPage(false);
    window.addEventListener("whatsapp-hide", onHide);
    window.addEventListener("whatsapp-show", onShow);
    return () => {
      window.removeEventListener("whatsapp-hide", onHide);
      window.removeEventListener("whatsapp-show", onShow);
    };
  }, []);

  const bottomOffset = isMobileView
    ? mobileSheetBottomRem > 0
      ? `${mobileSheetBottomRem}rem`
      : stickyCtaVisible
      ? "5.5rem"
      : "1.75rem"
    : stickyCtaVisible
    ? "5.5rem"
    : "1.75rem";

  function openWhatsApp() {
    const phone = "6589388171";
    const message =
      "Hi, I have some questions about HomeHelpy services from your website!";
    const encodedMessage = encodeURIComponent(message);
    const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;
    window.open(url, "_blank");
  }

  // Hide during the entire booking flow (steps 1-5 + result pages)
  if (pathname.startsWith("/book/")) return null;
  // Hide when a page signals it (e.g. package purchase steps)
  if (hiddenByPage) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: bottomOffset,
        right: "1.75rem",
        transition: "bottom 0.3s ease",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
      }}
    >
      <AnimatePresence>
        {hovered && (
          <MotionDiv
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{
              backgroundColor: "white",
              color: "#15803d",
              fontSize: "0.82rem",
              fontWeight: 700,
              padding: "0.4rem 0.85rem",
              borderRadius: "9999px",
              boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            Chat with us!
          </MotionDiv>
        )}
      </AnimatePresence>

      <MotionButton
        data-testid="whatsapp-float-button"
        aria-label="Open WhatsApp"
        onClick={openWhatsApp}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        animate={
          hovered
            ? { scale: [1, 1.08, 1, 1.08, 1], transition: { duration: 3.5, repeat: Infinity, ease: "easeInOut" } }
            : { scale: 1, transition: { duration: 0.3 } }
        }
        whileTap={{ scale: 0.94 }}
        style={{
          flexShrink: 0,
          width: "3.75rem",
          height: "3.75rem",
          borderRadius: "9999px",
          background: NAVBAR_GREEN_GRADIENT,
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 24px rgba(34, 197, 94, 0.4)",
        }}
      >
        <Icon as={FaWhatsapp} boxSize={7} color="white" />
      </MotionButton>
    </div>
  );
}
